
import React, { useState } from 'react';
import { Edit, Trash2, Calendar, User, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { useUpdateCampaignTask, useDeleteCampaignTask, type CampaignTask, type TaskStatus } from '@/hooks/useCampaignTasks';
import { EditTaskModal } from './EditTaskModal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/status/StatusBadge';

const priorityColors = {
  low: 'bg-gray-100 text-gray-800',
  medium: 'bg-orange-100 text-orange-800',
  high: 'bg-red-100 text-red-800',
  urgent: 'bg-purple-100 text-purple-800',
};

interface TaskCardProps {
  task: CampaignTask;
  campaignId: string;
}

export const TaskCard: React.FC<TaskCardProps> = ({ task, campaignId }) => {
  const [editingTask, setEditingTask] = useState(false);
  
  const updateTaskMutation = useUpdateCampaignTask();
  const deleteTaskMutation = useDeleteCampaignTask();

  const handleStatusChange = (newStatus: TaskStatus) => {
    updateTaskMutation.mutate({
      taskId: task.id,
      campaignId,
      taskData: { status: newStatus }
    });
  };

  const handleDeleteTask = () => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      deleteTaskMutation.mutate({ taskId: task.id, campaignId });
    }
  };

  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';

  return (
    <>
      <div className={`border rounded-lg p-4 hover:bg-gray-50 transition-colors ${isOverdue ? 'border-red-200 bg-red-50' : ''}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="font-medium text-gray-900">{task.title}</h4>
              <Badge className={priorityColors[task.priority]}>
                {task.priority}
              </Badge>
              {isOverdue && (
                <Badge variant="destructive">Overdue</Badge>
              )}
            </div>
            
            {task.description && (
              <p className="text-gray-600 text-sm mb-3 whitespace-pre-wrap">{task.description}</p>
            )}

            <div className="flex items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                <span>By {task.creator_name}</span>
              </div>
              
              {task.assigned_to_name && (
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  <span>Assigned to {task.assigned_to_name}</span>
                </div>
              )}
              
              {task.due_date && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Due {format(new Date(task.due_date), 'MMM d, yyyy')}</span>
                </div>
              )}

              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{format(new Date(task.created_at), 'MMM d, yyyy')}</span>
              </div>
            </div>

            {task.completed_at && (
              <div className="text-sm text-green-600 mt-2">
                Completed on {format(new Date(task.completed_at), 'MMM d, yyyy at h:mm a')}
                {task.completed_by_name && ` by ${task.completed_by_name}`}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 ml-4">
            <Select value={task.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditingTask(true)}
            >
              <Edit className="w-4 h-4" />
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDeleteTask}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <StatusBadge status={task.status} />
        </div>
      </div>

      {editingTask && (
        <EditTaskModal
          campaignId={campaignId}
          task={task}
          onClose={() => setEditingTask(false)}
        />
      )}
    </>
  );
};
